import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  AppBar, 
  Toolbar, 
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Button,
  Backdrop,
  CircularProgress,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  FitnessCenter as FitnessIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  BarChart as StatsIcon,
  AccountBalance as BillingIcon,
  Dashboard as DashboardIcon,
  NotificationsActive as NotificationsIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getCurrentUser, logout, isPartnerProfileComplete, getPartnerCheckIns, updateCheckInStatus } from '../../services/api';
import tuttoGymLogo from '../../images/tuttogym.jpeg';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import PartnerProfileForm from '../../components/PartnerProfileForm';
import { apiClient } from '../../services/api';

const PartnerDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [profileComplete, setProfileComplete] = useState(true);
  const [checkIns, setCheckIns] = useState({
    pending: [],
    accepted: [],
    rejected: []
  });
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [checkInToReject, setCheckInToReject] = useState(null);

  // Función para solicitar permisos de notificación
  const requestNotificationPermission = async () => {
    try {
      // Verificar si el navegador soporta notificaciones
      if (!('Notification' in window)) {
        console.log('Este navegador no soporta notificaciones de escritorio');
        return;
      }

      // Solicitar permiso
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        // Mostrar notificación de prueba
        new Notification('Notificaciones activadas', {
          body: 'Ahora recibirás notificaciones cuando lleguen nuevos check-ins',
          icon: '/favicon.ico'
        });
      } else {
        setNotificationsEnabled(false);
      }
    } catch (error) {
      console.error('Error al solicitar permisos de notificación:', error);
    }
  };

  useEffect(() => {
    // Verificar estado de notificaciones
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
    
    // Get current user data
    const user = getCurrentUser();
    if (!user || !user.email) {
      console.error('No user data found');
      navigate('/panel');
      return;
    }
    
    // Check if user has the correct role to access this dashboard
    if (user.role !== 'partner') {
      console.warn('Unauthorized access attempt to partner dashboard by', user.role);
      
      // Redirect user to their appropriate dashboard
      switch(user.role) {
        case 'customer':
          navigate('/panel/customer-dashboard');
          break;
        case 'company':
          navigate('/panel/company-dashboard');
          break;
        default:
          navigate('/panel');
          break;
      }
      return;
    }
    
    setCurrentUser(user);
    
    // Check if partner profile is complete
    const complete = isPartnerProfileComplete();
    
    // Si el perfil no está completo, redirigir al formulario de registro
    if (!complete) {
      navigate('/panel/partner-registration');
      return;
    }
    
    setProfileComplete(true);
    
    // Cargar los check-ins iniciales
    if (user.id) {
      loadCheckIns(user.id);
    }

    // Configurar intervalo de actualización para refrescar check-ins cada 30 segundos
    const refreshInterval = setInterval(() => {
      if (user.id) {
        console.log('Refrescando check-ins automáticamente...');
        loadCheckIns(user.id);
        setLastRefresh(Date.now());
      }
    }, 30000); // 30 segundos

    // Configurar listener para el evento personalizado newCheckIn
    const handleNewCheckIn = (event) => {
      const checkInData = event.detail;
      
      // Solo actualizar si el partner_id (partnerid) coincide con el usuario actual
      if (
        checkInData && 
        (checkInData.partnerid === user.id || checkInData.partner_id === user.id)
      ) {
        console.log('Nuevo check-in detectado para este partner:', checkInData);
        loadCheckIns(user.id);
        setLastRefresh(Date.now());
      }
    };

    // Agregar listener para el evento newCheckIn
    window.addEventListener('newCheckIn', handleNewCheckIn);

    // Exponer función de actualización global para uso externo (TestCheckins y otros)
    window.dashboardRefresh = () => {
      if (user.id) {
        console.log('Refresco manual del dashboard solicitado');
        loadCheckIns(user.id);
        setLastRefresh(Date.now());
      }
    };
    
    // Limpiar intervalo y listener al desmontar
    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('newCheckIn', handleNewCheckIn);
      delete window.dashboardRefresh;
    };
  }, [navigate]);

  // Cargar los check-ins pendientes del socio
  const loadCheckIns = async (partnerId) => {
    if (!partnerId) return;
    
    try {
      setIsLoading(true);
      // Obtener todos los check-ins y filtrar por el partner actual
      const response = await apiClient.get('/check-ins');
      
      if (response.data && response.data.status === 'success' && Array.isArray(response.data.data)) {
        // Filtrar los check-ins por el ID del partner actual
        const partnerCheckIns = response.data.data.filter(
          checkin => checkin.partnerid === partnerId.toString()
        );
        
        console.log('Check-ins received from server:', partnerCheckIns);
        
        // Separar check-ins por su status
        const pending = partnerCheckIns.filter(checkin => 
          !checkin.status || checkin.status === 'pending');
        const accepted = partnerCheckIns.filter(checkin => 
          checkin.status === 'approved' || checkin.status === 'accepted');
        const rejected = partnerCheckIns.filter(checkin => 
          checkin.status === 'rejected');
        
        console.log('Pending:', pending.length, 'Accepted:', accepted.length, 'Rejected:', rejected.length);
        
        // Comprobar si hay nuevos check-ins
        const previousPending = checkIns.pending || [];
        const newCheckIns = pending.filter(newCheckin => 
          !previousPending.some(oldCheckin => oldCheckin.id === newCheckin.id)
        );
        
        console.log('Nuevos check-ins detectados:', newCheckIns.length);
        
        // Marcar los nuevos check-ins con una propiedad isNew para animarlos
        const pendingWithNewFlag = pending.map(checkin => ({
          ...checkin,
          isNew: newCheckIns.some(newCheckin => newCheckin.id === checkin.id)
        }));
        
        setCheckIns({
          pending: pendingWithNewFlag,
          accepted: accepted,
          rejected: rejected
        });
        
        // Mostrar notificaciones para nuevos check-ins si están habilitadas
        if (newCheckIns.length > 0 && notificationsEnabled) {
          // Mostrar notificación para cada nuevo check-in
          newCheckIns.forEach(checkin => {
            showCheckInNotification(checkin);
          });
        }
      } else {
        console.error('Formato de respuesta incorrecto:', response);
        setCheckIns({
          pending: [],
          accepted: [],
          rejected: []
        });
      }
    } catch (error) {
      console.error('Error al cargar check-ins:', error);
      setCheckIns({
        pending: [],
        accepted: [],
        rejected: []
      });
      setSnackbar({
        open: true,
        message: t('dashboard.partner.loadCheckInsError', 'Error al cargar los check-ins'),
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
      setLastRefresh(Date.now());
    }
  };

  // Función para mostrar notificaciones de nuevos check-ins
  const showCheckInNotification = (checkin) => {
    if (!notificationsEnabled || !('Notification' in window)) return;
    
    try {
      // Título y cuerpo de la notificación
      const title = 'Nuevo Check-in';
      const body = `${checkin.username} ha realizado un check-in en tu gimnasio`;
      
      // Crear y mostrar la notificación
      const notification = new Notification(title, {
        body: body,
        icon: '/favicon.ico',
        tag: `checkin-${checkin.id}`, // Evitar duplicados
        requireInteraction: true // Mantener la notificación hasta que el usuario interactúe con ella
      });
      
      // Al hacer clic en la notificación, enfocar la ventana y destacar el check-in
      notification.onclick = () => {
        window.focus();
        // Hacer scroll hasta el check-in
        const checkInElement = document.getElementById(`checkin-${checkin.id}`);
        if (checkInElement) {
          checkInElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Aplicar un efecto de resaltado
          checkInElement.classList.add('highlight-checkin');
          setTimeout(() => {
            checkInElement.classList.remove('highlight-checkin');
          }, 3000);
        }
      };
    } catch (error) {
      console.error('Error al mostrar notificación:', error);
    }
  };

  // Manejar aceptación o rechazo de check-ins
  const handleCheckInStatusChange = async (checkInId, newStatus, comments = '') => {
    console.log(`🔹 Button clicked: handleCheckInStatusChange(${checkInId}, "${newStatus}", "${comments}")`);
    
    if (!checkInId) {
      console.error('❌ Cannot update check-in: Missing check-in ID');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Immediately update UI by moving the check-in between lists
      const checkInToUpdate = checkIns.pending.find(checkin => checkin.id === checkInId);
      
      if (checkInToUpdate) {
        console.log('✅ Found check-in to update:', checkInToUpdate);
        
        // Create updated state with the check-in moved to the appropriate list
        const updatedPending = checkIns.pending.filter(checkin => checkin.id !== checkInId);
        
        let updatedAccepted = [...checkIns.accepted];
        let updatedRejected = [...checkIns.rejected];
        
        // Add comments to the check-in if provided
        const updatedCheckIn = {
          ...checkInToUpdate,
          status: newStatus,
          comments: comments || checkInToUpdate.comments
        };
        
        console.log('Updated check-in object:', updatedCheckIn);
        
        // Add to the appropriate list based on status
        if (newStatus === 'accepted' || newStatus === 'approved') {
          updatedAccepted = [updatedCheckIn, ...updatedAccepted];
        } else if (newStatus === 'rejected') {
          updatedRejected = [updatedCheckIn, ...updatedRejected];
        }
        
        console.log('🔄 Updating UI state...');
        console.log('New pending count:', updatedPending.length);
        console.log('New accepted count:', updatedAccepted.length);
        console.log('New rejected count:', updatedRejected.length);
        
        // Update the state immediately
        setCheckIns({
          pending: updatedPending,
          accepted: updatedAccepted,
          rejected: updatedRejected
        });
      } else {
        console.warn(`⚠️ Could not find check-in with ID ${checkInId} in pending list`);
      }
      
      console.log(`🚀 Making API call to update check-in ${checkInId} status to ${newStatus}`);
      
      // Make the API call to update the server
      const response = await updateCheckInStatus(checkInId, newStatus, comments);
      
      console.log('📡 API response received:', response);
      
      if (response.status === 'error') {
        throw new Error(response.message);
      }
      
      console.log('✅ Check-in status updated successfully on server');
      
      // No need to reload all check-ins, as we've already updated the UI
      // But we can reload to ensure our state is in sync with the server
      // await loadCheckIns(currentUser?.id);
      
      setSnackbar({
        open: true,
        message: newStatus === 'accepted' 
          ? t('dashboard.partner.checkInAccepted', 'Check-in aceptado correctamente')
          : t('dashboard.partner.checkInRejected', 'Check-in rechazado correctamente'),
        severity: 'success',
      });
    } catch (error) {
      console.error('❌ Error al actualizar el check-in:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      
      // If there's an error, reload the check-ins to ensure UI is in sync with server
      await loadCheckIns(currentUser?.id);
      
      setSnackbar({
        open: true,
        message: t('dashboard.partner.updateCheckInError', 'Error al actualizar el estado del check-in'),
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Manejar el rechazo de un check-in con comentarios
  const handleRejectWithReason = () => {
    console.log('🔹 Reject with reason button clicked');
    
    if (checkInToReject) {
      console.log('Check-in to reject:', checkInToReject);
      console.log(`Calling handleCheckInStatusChange with ID: ${checkInToReject.id}, status: rejected, reason: ${rejectReason}`);
      
      handleCheckInStatusChange(checkInToReject.id, 'rejected', rejectReason);
      
      setRejectDialogOpen(false);
      setRejectReason('');
      setCheckInToReject(null);
    } else {
      console.error('❌ No check-in selected for rejection');
    }
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      // Close the menu if it exists
      if (typeof handleSettingsMenuClose === 'function') {
        handleSettingsMenuClose();
      }
      
      await logout();
      
      // Add a delay for the animation
      setTimeout(() => {
        setLoggingOut(false);
        // Redirect to main page instead of panel
        window.location.replace('/');
      }, 1000);
    } catch (error) {
      console.error('Logout failed:', error);
      setLoggingOut(false);
    }
  };

  const handleSettingsMenuOpen = (event) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    loadCheckIns();
  };

  const handleProfileUpdate = (updatedData) => {
    // Refresh check-ins data after profile update if needed
    if (currentUser?.id) {
      loadCheckIns(currentUser.id);
    }
  };

  // Cargar datos cuando el usuario cambia
  useEffect(() => {
    if (currentUser) {
      loadPartnerDetails();
      loadCheckIns();
    }
  }, [currentUser]);

  // Establecer el intervalo de recarga
  useEffect(() => {
    if (currentUser) {
      loadCheckIns();
      
      // Configurar intervalos de recarga
      const checkInsInterval = setInterval(() => {
        loadCheckIns();
      }, 30000); // Recargar cada 30 segundos

      return () => clearInterval(checkInsInterval);
    }
  }, [currentUser]);

  // Recargar check-ins cuando el componente gana el foco
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentUser) {
        loadCheckIns();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser]);

  // Actualizar check-ins según el estado del filtro
  useEffect(() => {
    if (currentUser) {
      loadCheckIns();
    }
  }, [selectedTab]);

  // Cargar los detalles del partner y los check-ins
  useEffect(() => {
    const loadData = async () => {
      if (currentUser) {
        try {
          setIsLoading(true);
          await loadPartnerDetails(currentUser.id);
          loadCheckIns();
        } catch (error) {
          console.error('Error al cargar los datos del partner:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadData();
  }, [currentUser]);

  // Recargar los datos cada 30 segundos
  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      loadCheckIns();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [currentUser]);

  // Recargar los datos cuando cambia la pestaña activa
  useEffect(() => {
    if (currentUser) {
      loadCheckIns();
    }
  }, [selectedTab]);

  // Botón de refresco manual
  const handleRefresh = () => {
    console.log('🔄 Refresco manual solicitado');
    if (currentUser?.id) {
      console.log(`Cargando check-ins para el partner ID: ${currentUser.id}`);
      loadCheckIns(currentUser.id);
    } else {
      console.error('❌ No se puede refrescar: ID de partner no disponible');
    }
  };

  // Function to load partner details
  const loadPartnerDetails = async (partnerId) => {
    if (!partnerId) return;
    
    try {
      // You can implement this to load additional partner details if needed
      console.log('Loading partner details for ID:', partnerId);
      // For now this is just a placeholder
    } catch (error) {
      console.error('Error loading partner details:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Estilos globales para animaciones */}
      <style jsx global>{`
        @keyframes highlight {
          0% { background-color: rgba(25, 118, 210, 0.4); }
          100% { background-color: transparent; }
        }
        .highlight-checkin {
          animation: highlight 3s ease-out;
        }
      `}</style>

      {/* Logout Animation Backdrop */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={loggingOut}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">{t('auth.loggingOut', 'Logging out...')}</Typography>
      </Backdrop>

      {/* App Bar with title and user info */}
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          {/* Logo */}
          <Box
            component="img"
            src={tuttoGymLogo}
            alt="TuttoGym Logo"
            sx={{ height: 40, mr: 2 }}
          />
          
          {/* Dashboard title with partner name */}
          <Typography variant="h6" component="div">
            {currentUser?.partnerName || 'Partner'} - {t('dashboard.partner.title', 'Partner Dashboard')}
          </Typography>
          
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Verification status chip */}
          <Chip
            label={t('dashboard.partner.pendingVerification', 'Pendiente de verificación')}
            color="warning"
            size="small"
            sx={{ mr: 2 }}
          />
          
          {/* Language selector */}
          <LanguageSwitcher />
          
          {/* User email */}
          {currentUser && (
            <Typography variant="body2" sx={{ mx: 2 }}>
              {currentUser.email}
            </Typography>
          )}
          
          {/* Logout button */}
          <IconButton 
            color="inherit" 
            onClick={handleSettingsMenuOpen}
            size="large"
            title={t('common.logout', 'Logout')}
          >
            <SettingsIcon />
          </IconButton>
          
          <Menu
            anchorEl={settingsMenuAnchor}
            open={Boolean(settingsMenuAnchor)}
            onClose={handleSettingsMenuClose}
          >
            <MenuItem onClick={handleLogout} disabled={loggingOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common.logout', 'Logout')}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Grid container spacing={3}>
          {/* Partner Profile Card */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Avatar 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  margin: '0 auto', 
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {currentUser?.partnerName?.charAt(0) || 'P'}
              </Avatar>
              <Typography variant="h5" sx={{ mt: 2 }}>
                {currentUser?.partnerName || t('dashboard.partner.defaultName', 'Partner Name')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentUser?.email}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}>
                <Chip
                  label={t('dashboard.partner.pendingVerification', 'Pendiente de verificación')}
                  color="warning"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.partner.id', 'Partner ID')}: {currentUser?.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.memberSince', 'Member since')}: {new Date().toLocaleDateString()}
              </Typography>
            </Paper>
            
            {/* Navigation */}
            <Paper sx={{ mt: 3 }}>
              <List>
                <ListItem button>
                  <ListItemIcon>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('dashboard.tabs.dashboard', 'Dashboard')} />
                </ListItem>
                <Divider />
                <ListItem button>
                  <ListItemIcon>
                    <HistoryIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('dashboard.partner.checkIns', 'Check-ins')} />
                </ListItem>
                <Divider />
                <ListItem button>
                  <ListItemIcon>
                    <StatsIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('dashboard.statistics', 'Analytics')} />
                </ListItem>
                <Divider />
                <ListItem button>
                  <ListItemIcon>
                    <BillingIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('dashboard.partner.billing', 'Billing')} />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={selectedTab} onChange={handleTabChange}>
                  <Tab label={t('dashboard.tabs.dashboard', 'Dashboard')} />
                  <Tab label={t('dashboard.tabs.profile', 'Edit Fitness Center')} />
                </Tabs>
              </Box>
              
              {selectedTab === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="h5" gutterBottom>
                    {t('dashboard.partner.title', 'Partner Dashboard')}
                  </Typography>
                  
                  <Alert severity="info" sx={{ mb: 3 }}>
                    {t('dashboard.partner.verificationPending', 'Su cuenta está pendiente de verificación. Le contactaremos a través de su correo electrónico para completar el proceso.')}
                  </Alert>
                  
                  <Typography paragraph>
                    {t('dashboard.partner.welcomeText', 'Welcome to your TuttoGym partner dashboard. Here you can manage your fitness centers, track member activities, and access analytics for your business.')}
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom>
                            {t('dashboard.partner.todayCheckIns', 'Today\'s Check-ins')}
                          </Typography>
                          <Typography variant="h3">
                            {checkIns.pending.length + checkIns.accepted.length}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  
                  {/* Sección de Check-ins pendientes */}
                  <Paper sx={{ mt: 3, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6">
                          {t('dashboard.partner.pendingCheckIns', 'Check-ins pendientes por aceptar')}
                        </Typography>
                        {!notificationsEnabled ? (
                          <Button 
                            size="small" 
                            startIcon={<NotificationsIcon />} 
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 2 }}
                            onClick={requestNotificationPermission}
                          >
                            {t('dashboard.partner.enableNotifications', 'Activar notificaciones')}
                          </Button>
                        ) : (
                          <Chip 
                            icon={<NotificationsIcon />}
                            label={`${checkIns.pending.length} pendientes`}
                            color={checkIns.pending.length > 0 ? "primary" : "default"}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button 
                          size="small" 
                          startIcon={<RefreshIcon />}
                          onClick={handleRefresh}
                        >
                          {t('dashboard.refresh', 'Refrescar')}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {t('dashboard.lastUpdate', 'Última actualización')}: {new Date(lastRefresh).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    </Box>
                    {checkIns.pending.length > 0 ? (
                      <List>
                        {checkIns.pending.map((checkin, index) => (
                          <React.Fragment key={index}>
                            <ListItem 
                              id={`checkin-${checkin.id}`}
                              sx={{ 
                                animation: checkin.isNew ? 'pulse 2s infinite' : 'none',
                                '@keyframes pulse': {
                                  '0%': { backgroundColor: 'transparent' },
                                  '50%': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                                  '100%': { backgroundColor: 'transparent' }
                                }
                              }}
                            >
                              {/* Debug info - remove in production */}
                              {console.log(`Rendering pending check-in: ${checkin.id}`, checkin)}
                              <ListItemText 
                                primary={checkin.username} 
                                secondary={new Date(checkin.timestamp).toLocaleString()}
                              />
                              {checkin.isNew && (
                                <Chip 
                                  label={t('dashboard.partner.new', 'Nuevo')} 
                                  color="primary" 
                                  size="small" 
                                  sx={{ ml: 1 }}
                                />
                              )}
                              <Button 
                                variant="contained" 
                                color="primary" 
                                size="small" 
                                sx={{ mr: 1 }}
                                onClick={() => {
                                  console.log(`Accept button clicked for checkin ID: ${checkin.id}`);
                                  handleCheckInStatusChange(checkin.id, 'accepted');
                                }}
                              >
                                Aceptar
                              </Button>
                              <Button 
                                variant="outlined" 
                                color="error" 
                                size="small"
                                onClick={() => {
                                  console.log(`Reject button clicked for checkin ID: ${checkin.id}`);
                                  setRejectDialogOpen(true);
                                  setCheckInToReject(checkin);
                                }}
                              >
                                Rechazar
                              </Button>
                            </ListItem>
                            {index < checkIns.pending.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.partner.noPendingCheckIns', 'No hay check-ins pendientes')}
                      </Typography>
                    )}
                  </Paper>
                  
                  {/* Sección de Check-ins aceptados */}
                  <Paper sx={{ mt: 3, p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      {t('dashboard.partner.acceptedCheckIns', 'Check-ins aceptados')}
                    </Typography>
                    {checkIns.accepted.length > 0 ? (
                      <List>
                        {checkIns.accepted.map((checkin, index) => (
                          <React.Fragment key={index}>
                            <ListItem>
                              <ListItemText 
                                primary={checkin.username} 
                                secondary={`${new Date(checkin.timestamp).toLocaleString()} - Aceptado`}
                              />
                            </ListItem>
                            {index < checkIns.accepted.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.partner.noAcceptedCheckIns', 'No hay check-ins aceptados')}
                      </Typography>
                    )}
                  </Paper>
                  
                  {/* Sección de Check-ins rechazados */}
                  <Paper sx={{ mt: 3, p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      {t('dashboard.partner.rejectedCheckIns', 'Check-ins rechazados')}
                    </Typography>
                    {checkIns.rejected.length > 0 ? (
                      <List>
                        {checkIns.rejected.map((checkin, index) => (
                          <React.Fragment key={index}>
                            <ListItem>
                              <ListItemText 
                                primary={checkin.username} 
                                secondary={`${new Date(checkin.timestamp).toLocaleString()} - Rechazado`}
                              />
                              {checkin.comments && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 2, display: 'block' }}>
                                  Motivo: {checkin.comments}
                                </Typography>
                              )}
                            </ListItem>
                            {index < checkIns.rejected.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.partner.noRejectedCheckIns', 'No hay check-ins rechazados')}
                      </Typography>
                    )}
                  </Paper>
                  
                  <Box sx={{ mt: 3 }}>
                    <Button variant="outlined" color="primary">
                      {t('dashboard.partner.viewAnalytics', 'Ver Análisis')}
                    </Button>
                  </Box>
                </Box>
              )}
              
              {selectedTab === 1 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="h5" gutterBottom>
                    {t('dashboard.profile.title', 'Manage Your Fitness Center')}
                  </Typography>
                  
                  <Typography paragraph>
                    {t('dashboard.profile.description', 'Update your fitness center information, location, operating hours, and services. This information will be visible to users searching for gyms.')}
                  </Typography>
                  
                  <PartnerProfileForm onUpdate={handleProfileUpdate} />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Reject Dialog */}
      <Dialog 
        open={rejectDialogOpen} 
        onClose={() => setRejectDialogOpen(false)}
        keepMounted={false}
        disableRestoreFocus
        aria-labelledby="reject-dialog-title"
      >
        <DialogTitle id="reject-dialog-title">Rechazar Check-in</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Por favor, proporciona un motivo para rechazar este check-in (opcional):
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            multiline
            rows={4}
            label="Motivo"
            variant="outlined"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleRejectWithReason} color="error" variant="contained">
            Rechazar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerDashboard; 